import React, { useState, useEffect } from 'react';
import { Calculator, Copy, Trash2, TrendingUp, TrendingDown, DollarSign, Coins, Percent, Info, CheckCircle2, Globe, AlertTriangle } from 'lucide-react';
import CryptoAffiliateBlock from '../../../components/CryptoAffiliateBlock';

// Tax rates by country - easily extensible
const TAX_RATES = {
  bulgaria: {
    name: 'Bulgaria',
    rate: 0.10, // 10%
    description: 'Flat 10% tax on crypto capital gains',
    currency: 'BGN/EUR',
    notes: 'Crypto profits are taxed as capital gains at a flat 10% rate. Losses can offset gains within the same tax year.'
  },
  usa: {
    name: 'United States',
    rate: 0.15, // Short-term can be up to 37%, long-term 0-20%, using 15% as average
    description: 'Capital gains tax (0-37% depending on holding period and income)',
    currency: 'USD',
    notes: 'Short-term gains (<1 year) taxed as ordinary income (10-37%). Long-term gains taxed at 0%, 15%, or 20%.'
  },
  uk: {
    name: 'United Kingdom',
    rate: 0.20, // 10% or 20% depending on income
    description: 'Capital Gains Tax at 10% or 20%',
    currency: 'GBP',
    notes: 'Basic rate taxpayers pay 10%, higher rate taxpayers pay 20%. Annual exempt amount applies.'
  },
  germany: {
    name: 'Germany',
    rate: 0.00, // Tax-free if held >1 year
    description: 'Tax-free if held over 1 year, otherwise income tax',
    currency: 'EUR',
    notes: 'Crypto held for more than 1 year is tax-free. Otherwise taxed at your personal income tax rate (up to 45%).'
  },
  portugal: {
    name: 'Portugal',
    rate: 0.28, // 28% for short-term
    description: '28% on short-term gains, 0% for long-term',
    currency: 'EUR',
    notes: 'Gains from crypto held less than 1 year taxed at 28%. Long-term holdings may be tax-free.'
  },
};

const CryptoTaxCalculatorTool = () => {
  const [buyPrice, setBuyPrice] = useState('');
  const [sellPrice, setSellPrice] = useState('');
  const [quantity, setQuantity] = useState('');
  const [fees, setFees] = useState('');
  const [country, setCountry] = useState('bulgaria');
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState('');

  // Real-time calculation
  useEffect(() => {
    calculate();
  }, [buyPrice, sellPrice, quantity, fees, country]);

  const calculate = () => {
    const buy = parseFloat(buyPrice);
    const sell = parseFloat(sellPrice);
    const qty = parseFloat(quantity);
    const feeAmount = parseFloat(fees) || 0;

    if (isNaN(buy) || isNaN(sell) || isNaN(qty) || buy < 0 || sell < 0 || qty <= 0 || feeAmount < 0) {
      setResult(null);
      return;
    }

    const totalInvestment = buy * qty;
    const totalRevenue = sell * qty;
    const grossProfit = totalRevenue - totalInvestment;
    const netProfitBeforeTax = grossProfit - feeAmount;
    
    // Tax calculation
    const taxRate = TAX_RATES[country]?.rate || 0;
    const taxableProfit = Math.max(0, netProfitBeforeTax); // Only tax profits, not losses
    const estimatedTax = taxableProfit * taxRate;
    const netProfitAfterTax = netProfitBeforeTax - estimatedTax;
    
    const roi = totalInvestment > 0 ? (netProfitBeforeTax / totalInvestment) * 100 : 0;
    const effectiveTaxRate = netProfitBeforeTax > 0 ? (estimatedTax / netProfitBeforeTax) * 100 : 0;

    setResult({
      totalInvestment,
      totalRevenue,
      grossProfit,
      netProfitBeforeTax,
      taxableProfit,
      estimatedTax,
      netProfitAfterTax,
      roi,
      effectiveTaxRate,
      taxRate: taxRate * 100,
      isProfitable: netProfitBeforeTax > 0,
      countryInfo: TAX_RATES[country]
    });
  };

  const copyResult = (value, label) => {
    navigator.clipboard.writeText(typeof value === 'number' ? value.toFixed(2) : value);
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  };

  const clearAll = () => {
    setBuyPrice('');
    setSellPrice('');
    setQuantity('');
    setFees('');
    setResult(null);
  };

  const formatCurrency = (value) => {
    return '$' + value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatPercent = (value) => {
    return value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '%';
  };

  // Quick scenarios
  const scenarios = [
    { label: 'BTC Profit', buy: '30000', sell: '45000', qty: '0.5', fees: '50' },
    { label: 'ETH Profit', buy: '2000', sell: '3000', qty: '2', fees: '25' },
    { label: 'Small Trade', buy: '100', sell: '150', qty: '10', fees: '5' },
  ];

  const applyScenario = (scenario) => {
    setBuyPrice(scenario.buy);
    setSellPrice(scenario.sell);
    setQuantity(scenario.qty);
    setFees(scenario.fees);
  };

  return (
    <div className="space-y-6" data-testid="crypto-tax-calculator">
      {/* Quick Scenarios */}
      <div className="flex flex-wrap gap-2">
        <span className="text-sm text-gray-400 mr-2">Quick Examples:</span>
        {scenarios.map((scenario, index) => (
          <button
            key={index}
            onClick={() => applyScenario(scenario)}
            className="px-3 py-1 text-xs bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-full text-gray-300 transition-colors"
            data-testid={`scenario-${index}`}
          >
            {scenario.label}
          </button>
        ))}
      </div>

      {/* Input Section */}
      <div className="p-4 bg-gray-800 rounded-lg space-y-4">
        <div className="flex items-center gap-2 mb-2">
          <Coins size={18} className="text-yellow-400" />
          <h3 className="text-sm font-medium text-gray-300">Trade Details</h3>
        </div>
        
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Buy Price ($)
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={buyPrice}
              onChange={(e) => setBuyPrice(e.target.value)}
              placeholder="e.g., 30000"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
              data-testid="buy-price-input"
            />
            <p className="text-xs text-gray-500 mt-1">Price per coin when you bought</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Sell Price ($)
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={sellPrice}
              onChange={(e) => setSellPrice(e.target.value)}
              placeholder="e.g., 45000"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
              data-testid="sell-price-input"
            />
            <p className="text-xs text-gray-500 mt-1">Price per coin when you sold</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Quantity (Coins)
            </label>
            <input
              type="number"
              step="any"
              min="0"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              placeholder="e.g., 0.5"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
              data-testid="quantity-input"
            />
            <p className="text-xs text-gray-500 mt-1">Number of coins/tokens sold</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Total Fees ($) <span className="text-gray-500">(optional)</span>
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={fees}
              onChange={(e) => setFees(e.target.value)}
              placeholder="e.g., 50"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
              data-testid="fees-input"
            />
            <p className="text-xs text-gray-500 mt-1">Exchange + network fees</p>
          </div>
        </div>

        {/* Country Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            <Globe size={14} className="inline mr-1" />
            Country / Tax Jurisdiction
          </label>
          <select
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-blue-500 focus:outline-none"
            data-testid="country-select"
          >
            {Object.entries(TAX_RATES).map(([key, value]) => (
              <option key={key} value={key}>
                {value.name} - {(value.rate * 100).toFixed(0)}% tax rate
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-500 mt-1">
            {TAX_RATES[country]?.description}
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2 pt-2">
          <button
            onClick={clearAll}
            className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-gray-300 transition-colors"
            data-testid="clear-btn"
          >
            <Trash2 size={16} />
            Clear
          </button>
        </div>
      </div>

      {/* Results Section */}
      {result && (
        <div className="space-y-4">
          {/* Main Result Cards */}
          <div className="grid md:grid-cols-2 gap-4">
            {/* Profit/Loss Card */}
            <div className={`p-6 rounded-lg border ${result.isProfitable ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  {result.isProfitable ? (
                    <TrendingUp className="text-green-400" size={20} />
                  ) : (
                    <TrendingDown className="text-red-400" size={20} />
                  )}
                  <span className="text-sm text-gray-400">Net Profit Before Tax</span>
                </div>
                <button
                  onClick={() => copyResult(result.netProfitBeforeTax, 'profit')}
                  className="p-1 hover:bg-gray-700 rounded transition-colors"
                >
                  {copied === 'profit' ? <CheckCircle2 size={16} className="text-green-400" /> : <Copy size={16} className="text-gray-400" />}
                </button>
              </div>
              <p className={`text-3xl font-bold ${result.isProfitable ? 'text-green-400' : 'text-red-400'}`}>
                {result.netProfitBeforeTax >= 0 ? '+' : ''}{formatCurrency(result.netProfitBeforeTax)}
              </p>
              <p className="text-sm text-gray-400 mt-1">ROI: {formatPercent(result.roi)}</p>
            </div>

            {/* Tax Card */}
            <div className="p-6 rounded-lg border bg-orange-500/10 border-orange-500/30">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Percent className="text-orange-400" size={20} />
                  <span className="text-sm text-gray-400">Estimated Tax ({result.countryInfo.name})</span>
                </div>
                <button
                  onClick={() => copyResult(result.estimatedTax, 'tax')}
                  className="p-1 hover:bg-gray-700 rounded transition-colors"
                >
                  {copied === 'tax' ? <CheckCircle2 size={16} className="text-green-400" /> : <Copy size={16} className="text-gray-400" />}
                </button>
              </div>
              <p className="text-3xl font-bold text-orange-400">
                {formatCurrency(result.estimatedTax)}
              </p>
              <p className="text-sm text-gray-400 mt-1">
                Tax rate: {formatPercent(result.taxRate)}
              </p>
            </div>
          </div>

          {/* Net Profit After Tax - Main Highlight */}
          <div className={`p-6 rounded-lg border-2 ${result.netProfitAfterTax >= 0 ? 'bg-blue-500/10 border-blue-500/50' : 'bg-red-500/10 border-red-500/50'}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <DollarSign className={result.netProfitAfterTax >= 0 ? 'text-blue-400' : 'text-red-400'} size={24} />
                <span className="text-lg font-medium text-gray-200">Net Profit After Tax</span>
              </div>
              <button
                onClick={() => copyResult(result.netProfitAfterTax, 'netAfterTax')}
                className="p-2 hover:bg-gray-700 rounded transition-colors"
              >
                {copied === 'netAfterTax' ? <CheckCircle2 size={18} className="text-green-400" /> : <Copy size={18} className="text-gray-400" />}
              </button>
            </div>
            <p className={`text-4xl font-bold ${result.netProfitAfterTax >= 0 ? 'text-blue-400' : 'text-red-400'}`}>
              {result.netProfitAfterTax >= 0 ? '+' : ''}{formatCurrency(result.netProfitAfterTax)}
            </p>
            <p className="text-sm text-gray-400 mt-2">
              This is your take-home profit after estimated taxes in {result.countryInfo.name}
            </p>
          </div>

          {/* Detailed Breakdown */}
          <div className="grid md:grid-cols-3 gap-4">
            <div className="p-4 bg-gray-800 rounded-lg">
              <p className="text-sm text-gray-400">Total Investment</p>
              <p className="text-xl font-semibold text-white">{formatCurrency(result.totalInvestment)}</p>
              <p className="text-xs text-gray-500 mt-1">Buy Price × Quantity</p>
            </div>

            <div className="p-4 bg-gray-800 rounded-lg">
              <p className="text-sm text-gray-400">Total Revenue</p>
              <p className="text-xl font-semibold text-white">{formatCurrency(result.totalRevenue)}</p>
              <p className="text-xs text-gray-500 mt-1">Sell Price × Quantity</p>
            </div>

            <div className="p-4 bg-gray-800 rounded-lg">
              <p className="text-sm text-gray-400">Fees Paid</p>
              <p className="text-xl font-semibold text-orange-400">{formatCurrency(parseFloat(fees) || 0)}</p>
              <p className="text-xs text-gray-500 mt-1">Exchange + network</p>
            </div>
          </div>

          {/* Tax Calculation Breakdown */}
          <div className="p-4 bg-gray-800 rounded-lg">
            <div className="flex items-center gap-2 mb-3">
              <Calculator size={16} className="text-purple-400" />
              <h4 className="text-sm font-medium text-gray-300">Tax Calculation Breakdown</h4>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-400">Gross Profit (Revenue - Investment)</span>
                <span className="text-white">{formatCurrency(result.grossProfit)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Less: Trading Fees</span>
                <span className="text-orange-400">- {formatCurrency(parseFloat(fees) || 0)}</span>
              </div>
              <div className="flex justify-between border-t border-gray-700 pt-2">
                <span className="text-gray-400">Taxable Profit</span>
                <span className="text-white font-medium">{formatCurrency(result.taxableProfit)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Tax Rate ({result.countryInfo.name})</span>
                <span className="text-white">{formatPercent(result.taxRate)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Estimated Tax Owed</span>
                <span className="text-orange-400 font-medium">{formatCurrency(result.estimatedTax)}</span>
              </div>
              <div className="flex justify-between border-t border-gray-700 pt-2">
                <span className="text-gray-200 font-medium">Net Profit After Tax</span>
                <span className={`font-bold ${result.netProfitAfterTax >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {formatCurrency(result.netProfitAfterTax)}
                </span>
              </div>
            </div>
          </div>

          {/* Country Tax Info */}
          <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
            <div className="flex items-center gap-2 mb-2">
              <Globe size={16} className="text-blue-400" />
              <h4 className="text-sm font-medium text-gray-300">Tax Info: {result.countryInfo.name}</h4>
            </div>
            <p className="text-sm text-gray-400">{result.countryInfo.notes}</p>
          </div>

          {/* Affiliate Block */}
          <CryptoAffiliateBlock 
            profit={result.grossProfit} 
            cryptoName="crypto"
            toolName="Crypto Tax Calculator"
            toolSlug="crypto-tax-calculator"
          />
        </div>
      )}

      {/* Disclaimer */}
      <div className="p-4 bg-yellow-500/10 rounded-lg border border-yellow-500/30">
        <div className="flex items-start gap-3">
          <AlertTriangle size={20} className="text-yellow-400 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-yellow-400 mb-1">Important Disclaimer</h4>
            <p className="text-sm text-gray-400">
              This calculator provides <strong>estimates only</strong> and is not financial or tax advice. 
              Tax laws are complex and vary by jurisdiction, holding period, and individual circumstances. 
              Always consult a qualified tax professional or accountant for accurate tax calculations and filing.
            </p>
          </div>
        </div>
      </div>

      {/* Formula Reference */}
      <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
        <div className="flex items-center gap-2 mb-3">
          <Calculator size={16} className="text-purple-400" />
          <h4 className="text-sm font-medium text-gray-300">Formulas Used</h4>
        </div>
        <div className="grid md:grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-gray-400">Taxable Profit:</p>
            <code className="text-purple-400">(Sell - Buy) × Qty - Fees</code>
          </div>
          <div>
            <p className="text-gray-400">Tax Owed:</p>
            <code className="text-purple-400">Taxable Profit × Tax Rate</code>
          </div>
          <div>
            <p className="text-gray-400">Net After Tax:</p>
            <code className="text-purple-400">Taxable Profit - Tax Owed</code>
          </div>
          <div>
            <p className="text-gray-400">ROI:</p>
            <code className="text-purple-400">(Profit / Investment) × 100</code>
          </div>
        </div>
      </div>

      {/* SEO Content Section */}
      <div className="space-y-8 pt-6 border-t border-gray-700">
        
        {/* What is Section */}
        <section>
          <h2 className="text-2xl font-bold text-white mb-4">What is a Crypto Tax Calculator?</h2>
          <div className="prose prose-invert max-w-none text-gray-300 space-y-4">
            <p>
              A <strong>Crypto Tax Calculator</strong> is an essential tool for cryptocurrency investors that estimates 
              the taxes owed on your crypto gains. As governments worldwide establish clearer regulations on digital 
              assets, understanding your tax obligations has become crucial for every crypto trader and investor.
            </p>
            <p>
              Our calculator supports multiple countries and their specific tax rates, including special provisions 
              like Bulgaria's flat 10% capital gains tax and the United States' differentiated short-term vs. long-term 
              rates. It accounts for trading fees (which are often tax-deductible), calculates your cost basis, and 
              shows both your gross profit and net profit after estimated taxes.
            </p>
            <p>
              Whether you're a casual investor who made a few trades or an active trader with hundreds of transactions, 
              understanding your tax liability helps you plan better, avoid surprises during tax season, and potentially 
              optimize your tax strategy through legitimate means like tax-loss harvesting.
            </p>
          </div>
        </section>

        {/* How to Use Section */}
        <section>
          <h2 className="text-2xl font-bold text-white mb-4">How to Use the Crypto Tax Calculator</h2>
          <div className="space-y-3">
            <div className="flex gap-4 p-4 bg-gray-800/50 rounded-lg border border-gray-700">
              <div className="w-8 h-8 rounded-full bg-purple-500/20 text-purple-400 flex items-center justify-center font-bold flex-shrink-0">1</div>
              <div>
                <h3 className="font-semibold text-white">Select Your Country</h3>
                <p className="text-gray-400 text-sm">Choose your tax jurisdiction to apply the correct capital gains tax rate. Rates vary significantly — from 0% in some countries to 37%+ in others.</p>
              </div>
            </div>
            <div className="flex gap-4 p-4 bg-gray-800/50 rounded-lg border border-gray-700">
              <div className="w-8 h-8 rounded-full bg-purple-500/20 text-purple-400 flex items-center justify-center font-bold flex-shrink-0">2</div>
              <div>
                <h3 className="font-semibold text-white">Enter Buy & Sell Prices</h3>
                <p className="text-gray-400 text-sm">Input the price per coin when you bought and when you sold. The difference determines your capital gain or loss.</p>
              </div>
            </div>
            <div className="flex gap-4 p-4 bg-gray-800/50 rounded-lg border border-gray-700">
              <div className="w-8 h-8 rounded-full bg-purple-500/20 text-purple-400 flex items-center justify-center font-bold flex-shrink-0">3</div>
              <div>
                <h3 className="font-semibold text-white">Enter Quantity Traded</h3>
                <p className="text-gray-400 text-sm">Specify the number of coins in this transaction. You can enter fractional amounts for partial positions.</p>
              </div>
            </div>
            <div className="flex gap-4 p-4 bg-gray-800/50 rounded-lg border border-gray-700">
              <div className="w-8 h-8 rounded-full bg-purple-500/20 text-purple-400 flex items-center justify-center font-bold flex-shrink-0">4</div>
              <div>
                <h3 className="font-semibold text-white">Add Trading Fees</h3>
                <p className="text-gray-400 text-sm">Include exchange fees, network fees, and gas costs. These are typically tax-deductible and reduce your taxable profit.</p>
              </div>
            </div>
            <div className="flex gap-4 p-4 bg-gray-800/50 rounded-lg border border-gray-700">
              <div className="w-8 h-8 rounded-full bg-purple-500/20 text-purple-400 flex items-center justify-center font-bold flex-shrink-0">5</div>
              <div>
                <h3 className="font-semibold text-white">View Tax Breakdown</h3>
                <p className="text-gray-400 text-sm">See your taxable profit, estimated tax owed, net profit after tax, and effective tax rate. Use this for tax planning.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Example Calculation Section */}
        <section>
          <h2 className="text-2xl font-bold text-white mb-4">Example Crypto Tax Calculation</h2>
          <div className="p-5 bg-gray-800 rounded-lg border border-gray-700">
            <h3 className="font-semibold text-white mb-3">Scenario: Bitcoin Sale in the United States</h3>
            <div className="space-y-3 text-gray-300">
              <p>Let's calculate taxes on a profitable Bitcoin trade for a US taxpayer in the 24% bracket:</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li><strong>Buy Price:</strong> $30,000 per BTC</li>
                <li><strong>Sell Price:</strong> $45,000 per BTC</li>
                <li><strong>Quantity:</strong> 1 BTC</li>
                <li><strong>Trading Fees:</strong> $150</li>
                <li><strong>Tax Rate:</strong> 24% (short-term capital gains)</li>
              </ul>
              <div className="mt-4 p-4 bg-gray-900 rounded-lg">
                <p className="font-mono text-sm">
                  <span className="text-gray-400">Cost Basis:</span> <span className="text-white">$30,000 × 1 = $30,000</span><br/>
                  <span className="text-gray-400">Sale Proceeds:</span> <span className="text-white">$45,000 × 1 = $45,000</span><br/>
                  <span className="text-gray-400">Gross Profit:</span> <span className="text-green-400">$45,000 - $30,000 = $15,000</span><br/>
                  <span className="text-gray-400">Taxable Profit:</span> <span className="text-green-400">$15,000 - $150 (fees) = $14,850</span><br/>
                  <span className="text-gray-400">Tax Owed:</span> <span className="text-red-400">$14,850 × 24% = $3,564</span><br/>
                  <span className="text-gray-400">Net After Tax:</span> <span className="text-green-400">$14,850 - $3,564 = $11,286</span>
                </p>
              </div>
              <p className="text-sm text-gray-400 mt-3">
                From your $15,000 profit, you'd owe approximately $3,564 in taxes, leaving you with $11,286 net profit. 
                Note: Long-term holders (1+ year) may qualify for lower capital gains rates of 0%, 15%, or 20%.
              </p>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section>
          <h2 className="text-2xl font-bold text-white mb-4">Frequently Asked Questions</h2>
          <div className="space-y-4">
            <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
              <h3 className="font-semibold text-white mb-2">When do I owe taxes on cryptocurrency?</h3>
              <p className="text-gray-400 text-sm">
                In most countries, you owe taxes when you have a "taxable event": selling crypto for fiat, trading crypto 
                for another crypto, or using crypto to purchase goods/services. Simply holding crypto or transferring 
                between your own wallets is typically not taxable.
              </p>
            </div>
            <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
              <h3 className="font-semibold text-white mb-2">What's the difference between short-term and long-term capital gains?</h3>
              <p className="text-gray-400 text-sm">
                In the US and many countries, assets held for less than 1 year are taxed as short-term gains (often at 
                your ordinary income rate, up to 37%). Assets held longer than 1 year qualify for long-term capital gains 
                rates, which are typically lower (0%, 15%, or 20% in the US).
              </p>
            </div>
            <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
              <h3 className="font-semibold text-white mb-2">Can I deduct crypto losses?</h3>
              <p className="text-gray-400 text-sm">
                Yes! In most jurisdictions, crypto losses can offset gains. If your losses exceed gains, you may be able 
                to deduct up to $3,000 against ordinary income (US) and carry forward remaining losses. This is called 
                "tax-loss harvesting" and is a legitimate tax optimization strategy.
              </p>
            </div>
            <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
              <h3 className="font-semibold text-white mb-2">Are trading fees tax-deductible?</h3>
              <p className="text-gray-400 text-sm">
                Generally yes. Exchange fees, network fees, and gas costs can be added to your cost basis (reducing gains) 
                or deducted as investment expenses. Keep detailed records of all fees paid, including those for failed 
                transactions.
              </p>
            </div>
            <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
              <h3 className="font-semibold text-white mb-2">Which countries have 0% crypto tax?</h3>
              <p className="text-gray-400 text-sm">
                Some countries with favorable crypto tax treatment include Portugal, UAE, Singapore (no capital gains tax), 
                Germany (tax-free after 1 year hold), and Switzerland (tax-free for individual investors). However, tax 
                laws change frequently — always verify current regulations.
              </p>
            </div>
          </div>
        </section>
      </div>

      {/* Tips Section */}
      <div className="p-4 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-lg border border-blue-500/20">
        <h4 className="text-sm font-medium text-gray-200 mb-2">💡 Tax Tips for Crypto Traders</h4>
        <ul className="text-sm text-gray-400 space-y-1 list-disc list-inside">
          <li>Keep detailed records of all buy/sell transactions with dates and prices</li>
          <li>Trading fees are often tax-deductible - save your receipts</li>
          <li>Losses can offset gains in many jurisdictions - track losing trades too</li>
          <li>Holding period matters - long-term gains often have lower tax rates</li>
          <li>Consider tax-loss harvesting strategies before year-end</li>
        </ul>
      </div>
    </div>
  );
};

export default CryptoTaxCalculatorTool;
